<div class="messages-container"
     [class.messages-container--broadcast-composer]="context === 'broadcast'">
    <!-- Chat Participant Info -->
    @if (!(context === 'chatRoom' && !source) && context !== 'broadcast') {
        <div class="messages-header mb-3">
            @if (!source) {
                <!-- User Picker (when no source selected) -->
                <div class="user-picker-container">
                    <h5 class="mb-3 fw-bold">New message</h5>
                <div class="form-search position-relative">
                    <span class="search-icon">
                        <img src="assets/common/ic_search.svg" alt="Search">
                    </span>
                    <input #userSearchInput
                        type="text" 
                        class="form-control" 
                        placeholder="Type the name of the client"
                        [(ngModel)]="userSearchQuery"
                        (ngModelChange)="onUserSearchChange($event)"
                        (focus)="wasInputFocused = true"
                        (blur)="wasInputFocused = false"
                        [disabled]="searchingUsers" />
                    <div *ngIf="searchingUsers" class="search-loading">
                        <span class="spinner-border spinner-border-sm"></span>
                    </div>
                </div>
                
                <!-- User Search Results Dropdown -->
                <div *ngIf="userSearchResults.length > 0" class="user-search-results">
                    <div *ngFor="let customer of userSearchResults; trackBy: trackByCustomerId" 
                         class="user-search-result-item"
                         (click)="onUserSelected(customer)">
                         <div class="user-avatar">
                            @if (customer.avatar) {
                                <img [src]="customer.avatar" [alt]="customer.first_name + ' ' + customer.last_name" class="avatar-image">
                            } @else {
                                <div class="avatar-circle">
                                    <span class="avatar-initials">{{ getInitials(customer.first_name, customer.last_name) }}</span>
                                </div>
                            }
                        </div>
                        <div class="user-search-result-name">
                            {{ getUserFullName(customer) }}
                        </div>
                    </div>
                </div>
            </div>
        } @else {
            <!-- Source Info (when profile, chat or chatRoom is selected) -->
            <div class="d-flex align-items-center justify-content-between gap-3">
                <div class="d-flex align-items-center gap-0">
                    @if (source === 'chatRoom' && chatRoom) {
                        <!-- Chat Room Info -->
                        <div class="user-avatar">
                            @if (chatRoom.image?.thumbnail_url) {
                                <img [src]="chatRoom.image.thumbnail_url" [alt]="chatRoom.name" class="avatar-image">
                            } @else {
                                <div class="avatar-circle">
                                    <span class="avatar-initials">{{ getChatRoomInitials(chatRoom.name) }}</span>
                                </div>
                            }
                        </div>

                        <div>
                            <h5 class="mb-0 ml-0">{{ chatRoom.name }}</h5>
                            <p class="mb-0 text-muted text-sm">{{ chatRoom.member_count || 0 }} members</p>
                        </div>
                    } @else if (user) {
                        <!-- User Info -->
                        <div class="user-avatar">
                            @if (user.avatar) {
                                <img [src]="user.avatar" [alt]="user.first_name + ' ' + user.last_name" class="avatar-image">
                            } @else {
                                <div class="avatar-circle">
                                    <span class="avatar-initials">{{ getInitials(user.first_name, user.last_name) }}</span>
                                </div>
                            }
                        </div>
                        
                        <div>
                            <h5 class="mb-0 ml-0">{{ user.first_name }} {{ user.last_name }}</h5>
                            <div class="user-tags">
                                <span *ngFor="let tag of userTags" class="tag-pill">
                                    <img src="assets/common/ic_tag.svg" alt="" class="tag-icon">
                                    <span class="tag-name">{{ tag.name }}</span>
                                </span>
                            </div>
                        </div>
                    }
                </div>

                @if (source === 'chat' && chat) {
                    <!-- Thread Actions -->
                    <div class="d-flex align-items-center gap-2 message-header-actions">
                        <button *ngIf="user && !user.roles?.includes('tester')"
                                type="button"
                                class="header-action-btn"
                                (click)="openUserDetails()"
                                title="Open client profile">
                            <img src="assets/common/ic_new_tab.svg" alt="Open in new tab">
                        </button>
                        <button type="button"
                                class="header-action-btn"
                                [class.active]="isPinned"
                                (click)="togglePinned()"
                                [attr.aria-pressed]="isPinned"
                                title="{{ isPinned ? 'Unpin conversation' : 'Pin conversation' }}">
                            <img [src]="isPinned ? 'assets/messaging/ic_pinned.svg' : 'assets/messaging/ic_pinned_inactive.svg'"
                                [alt]="isPinned ? 'Pinned' : 'Not pinned'">
                        </button>
                        <button type="button"
                                class="header-action-btn"
                                [class.active]="isFavorited"
                                (click)="toggleFavorited()"
                                [attr.aria-pressed]="isFavorited"
                                title="{{ isFavorited ? 'Remove from favorites' : 'Add to favorites' }}">
                            <img [src]="isFavorited ? 'assets/messaging/ic_favorited.svg' : 'assets/messaging/ic_favorited_inactive.svg'"
                                [alt]="isFavorited ? 'Favorited' : 'Not favorited'">
                        </button>
                        <button type="button"
                                class="header-action-btn"
                                [class.active]="isArchived"
                                (click)="toggleArchived()"
                                [attr.aria-pressed]="isArchived"
                                title="{{ isArchived ? 'Unarchive conversation' : 'Archive conversation' }}">
                            <img [src]="isArchived ? 'assets/messaging/ic_unarchive.svg' : 'assets/messaging/ic_archive.svg'"
                                [alt]="isArchived ? 'Archived' : 'Not archived'">
                        </button>
                    </div>
                } @else if (source === 'chatRoom' && chatRoom) {
                    @if (chatRoom.type === 'custom') {
                        <!-- Custom Chat Room Actions -->
                        <div class="d-flex align-items-center gap-2 message-header-actions">
                            <button type="button"
                                    class="header-action-btn"
                                    [class.active]="isPinned"
                                    (click)="togglePinned()"
                                    [attr.aria-pressed]="isPinned"
                                    title="{{ isPinned ? 'Unpin conversation' : 'Pin conversation' }}">
                                <img [src]="isPinned ? 'assets/messaging/ic_pinned.svg' : 'assets/messaging/ic_pinned_inactive.svg'"
                                    [alt]="isPinned ? 'Pinned' : 'Not pinned'">
                            </button>
                            <button type="button"
                                    class="header-action-btn"
                                    [class.active]="isFavorited"
                                    (click)="toggleFavorited()"
                                    [attr.aria-pressed]="isFavorited"
                                    title="{{ isFavorited ? 'Remove from favorites' : 'Add to favorites' }}">
                                <img [src]="isFavorited ? 'assets/messaging/ic_favorited.svg' : 'assets/messaging/ic_favorited_inactive.svg'"
                                    [alt]="isFavorited ? 'Favorited' : 'Not favorited'">
                            </button>
                            <button type="button"
                                    class="header-action-btn"
                                    [class.active]="isArchived"
                                    (click)="toggleArchived()"
                                    [attr.aria-pressed]="isArchived"
                                    title="{{ isArchived ? 'Unarchive conversation' : 'Archive conversation' }}">
                                <img [src]="isArchived ? 'assets/messaging/ic_unarchive.svg' : 'assets/messaging/ic_archive.svg'"
                                    [alt]="isArchived ? 'Archived' : 'Not archived'">
                            </button>

                            @if (canManageCustomChatRoom()) {
                                <div class="position-relative">
                                    <button type="button"
                                            class="header-action-btn"
                                            [class.active]="showGroupChatMenu"
                                            (click)="toggleCustomGroupChatMenu($event)"
                                            title="More options">
                                        <img src="assets/common/ic_three_dots.svg" alt="More options">
                                    </button>
                                    
                                    <!-- Custom Group Chat Menu Popup -->
                                    <div *ngIf="showGroupChatMenu" 
                                        class="group-chat-menu-popup"
                                        (click)="$event.stopPropagation()">
                                        <button type="button"
                                                class="group-chat-menu-item"
                                                (click)="onEditCustomGroupChat()">
                                            <img src="assets/common/ic_edit.svg" alt="Edit">
                                            <span>Edit group chat</span>
                                        </button>
                                        <button type="button"
                                                class="group-chat-menu-item"
                                                (click)="onManageCustomGroupChatMembers()">
                                            <img src="assets/messaging/ic_manage_members.svg" alt="Manage members">
                                            <span>Manage members</span>
                                        </button>
                                        <button type="button"
                                                class="group-chat-menu-item group-chat-menu-item-danger"
                                                (click)="onDeleteCustomGroupChat()">
                                            <img src="assets/common/ic_delete_destructive.svg" alt="Delete">
                                            <span>Delete group chat</span>
                                        </button>
                                    </div>
                                </div>
                            } @else if (canEditCustomChatRoom()) {
                                <!--
                                <div class="d-flex align-items-center gap-2 message-header-actions">
                                    <button type="button"
                                            class="header-action-btn"
                                            (click)="onEditCustomGroupChat()"
                                            title="Edit chat">
                                        <img src="assets/common/ic_edit_m_size.svg" alt="Edit">
                                    </button>
                                </div>
                                -->
                            }
                        </div>
                    } @else if (chatRoom.type === 'system') {
                        <!-- System Chat Room Actions -->
                        <div class="d-flex align-items-center gap-2 message-header-actions">
                            <button type="button"
                                    class="header-action-btn"
                                    (click)="onEditSystemChatRoom()"
                                    title="Edit chat">
                                <img src="assets/common/ic_edit_m_size.svg" alt="Edit">
                            </button>
                        </div>
                    }
                }
            </div>
        }
        </div>
    }

    <!-- Message Image / Video Lightbox (custom, scoped to messages tab) -->
    <div *ngIf="showImageModal"
         class="media-lightbox-backdrop"
         (click)="closeImageModal()">
        <div class="media-lightbox"
             role="dialog"
             aria-modal="true"
             (click)="$event.stopPropagation()">
            <button class="media-lightbox-close"
                    type="button"
                    (click)="closeImageModal()"
                    title="Close">
                <i class="ti ti-x"></i>
            </button>

            <div *ngIf="selectedImage" class="media-lightbox-body">
                <img *ngIf="!isVideoFile(selectedImage)"
                     [src]="selectedImage.image_url"
                     [alt]="selectedImage.label || 'Image'"
                     class="media-lightbox-image">

                <video *ngIf="isVideoFile(selectedImage)"
                       [src]="selectedImage.image_url"
                       autoplay
                       controls
                       class="media-lightbox-video">
                    Your browser does not support the video tag.
                </video>
            </div>
        </div>
    </div>

    <!-- Manage members modal (chat room) -->
    @if (showManageMembersModal && chatRoom) {
        <app-manage-members-modal
            [chatRoomId]="chatRoom.id"
            [memberCount]="chatRoom.member_count ?? 0"
            (close)="onCloseManageMembersModal()"
            (addNewMembers)="onAddNewMembersFromManageModal()">
        </app-manage-members-modal>
    }

    <!-- Messages List -->
    <div class="messages-wrapper">
        @if (context !== 'broadcast') {
        <div class="messages-list" #messagesList>
            <button *ngIf="showLoadMoreMessages && !loadingMessages"
                    class="btn btn-outline-secondary load-more-messages"
                    (click)="loadMoreMessages()">Load previous messages</button>
            
            <div *ngIf="loadingMessages" class="text-center py-4">
                <p class="text-muted">Loading messages...</p>
            </div>

            @if (!source) {
                <!-- No source selected empty state -->
                <div class="no-messages text-center py-5">
                    <img src="../../../../assets/messaging/ic_no_messages.svg" />
                    @if (contextType === 'chatRoom') {
                        <h6 class="mb-2">No group chat selected</h6>
                        @if (isCoach) {
                            <p class="text-muted mb-4">
                                Select an existing group chat from the list on the left.
                            </p>
                        } @else {
                            <p class="text-muted mb-4">
                                Select an existing group chat from the list on the left, or create a new custom group chat using the button below.
                            </p>
                            <button class="btn btn-primary" (click)="createNewChatRoom.emit()">
                                Create new custom chat
                            </button>
                        }
                    } @else if (contextType === 'chat') {
                        <h6 class="mb-2">No user selected</h6>
                        <p class="text-muted">
                            Select a user from the list on the left to start a conversation.
                        </p>
                    } @else {
                        <h6 class="mb-2">No messages yet</h6>
                        <p class="text-muted">
                            You still haven't sent any message to this user.
                        </p>
                    }
                </div>
            } @else if (!loadingMessages && messages.length === 0) {
                <!-- Source selected but no messages -->
                <div class="no-messages text-center py-5">
                    <img src="../../../../assets/messaging/ic_no_messages.svg" />
                    <h6 class="mb-2">No messages yet</h6>
                    @if (source === 'chatRoom') {
                        <p class="text-muted">
                            This group chat doesn't have any messages yet. Send the first message to start the conversation.
                        </p>
                    } @else {
                        <p class="text-muted">
                            You still haven't sent any message to this user.
                        </p>
                    }
                </div>
            }

            <ng-template #objectAttachmentCard let-attachment="attachment">
                <div class="message-object-attachment-card">
                    <div class="message-object-attachment-thumb">
                        <img *ngIf="getObjectAttachmentImageUrl(attachment) as imageUrl"
                             [src]="imageUrl"
                             [alt]="getObjectAttachmentName(attachment)"
                             class="message-object-attachment-image">
                        <div *ngIf="!getObjectAttachmentImageUrl(attachment)"
                             class="message-object-attachment-placeholder"></div>
                        <div *ngIf="getObjectAttachmentIcon(attachment) as icon"
                             class="message-object-attachment-type-badge">
                            <img [src]="icon" alt="">
                        </div>
                    </div>
                    <div class="message-object-attachment-content">
                        <span class="message-object-attachment-title">{{ getObjectAttachmentName(attachment) }}</span>
                        <span *ngIf="getObjectAttachmentMeta(attachment) as meta"
                              class="message-object-attachment-type">{{ meta }}</span>
                    </div>
                </div>
            </ng-template>

            <ng-container *ngFor="let message of messages; let i = index">
                <!-- Date Separator -->
                <div *ngIf="shouldShowDateSeparator(message, i)" class="date-separator">
                    <span>{{ getDateSeparatorLabel(message) }}</span>
                </div>

                <!-- Message Item -->
                <div class="message-item" [ngClass]="{
                    'message-owner': message.is_my_message,
                    'message-user': !message.is_my_message,
                    'message-with-author-name': source === 'chatRoom' && !message.is_my_message && message.author?.name
                }">
                    <!-- User Avatar (left side for user messages) -->
                    <div *ngIf="!message.is_my_message" class="message-avatar">
                        <img *ngIf="message.author.avatar" 
                             [src]="message.author.avatar" 
                             [alt]="message.author.name"
                             class="avatar-image">
                        <div *ngIf="!message.author.avatar" class="avatar-circle">
                            <span class="avatar-initials">{{ getMessageInitials(message) }}</span>
                        </div>
                    </div>
                    
                    <div class="message-wrapper">
                        <!-- Author name (chat rooms only, left-side messages) -->
                        <span *ngIf="source === 'chatRoom' && !message.is_my_message && message.author?.name" 
                              class="message-author-name">{{ message.author.name }}</span>
                        <!-- Message Bubble Row (bubble + actions) -->
                        <div class="message-bubble-row" *ngIf="message.message">
                            <!-- Text Message Bubble -->
                            <div class="message-bubble" [ngClass]="{'message-deleted': message.is_message_deleted}">
                                <div class="message-content">
                                    <p *ngIf="!message.is_message_deleted"
                                       [innerHTML]="getMessageWithLinks(message.message)">
                                    </p>

                                    <div *ngIf="message.is_message_deleted" class="deleted-message-indicator">
                                        <i class="ti ti-ban"></i>
                                        <span>{{ message.message }}</span>
                                    </div>
                                    
                                    <p *ngIf="!message.is_message_deleted && !message.message && (!message.attachments || message.attachments.length === 0)" 
                                       class="text-muted deleted-message">
                                        Attachment deleted
                                    </p>
                                </div>
                            </div>
                            
                            <!-- Message Actions -->
                            <div class="message-actions" *ngIf="message.message && !message.is_message_deleted">
                                <button class="btn-icon-sm" (click)="replyToMessage(message)" title="Reply">
                                    <img src="assets/messaging/ic_reply.svg" alt="Reply">
                                </button>
                                <!--
                                <button *ngIf="!message.is_my_message && message.is_message_read !== false" 
                                        class="btn-icon-sm" 
                                        (click)="markMessageAsUnread(message)"
                                        title="Mark as unread">
                                    <img src="assets/messaging/ic_mark_as_unread.svg" alt="Mark as unread">
                                </button>
                                -->
                                <button class="btn-icon-sm" 
                                        (click)="toggleReactionPicker(message.id, $event)"
                                        [class.active]="showReactionPicker === message.id"
                                        title="Add reaction">
                                    <img src="assets/messaging/ic_emoji.svg" alt="Add reaction">
                                </button>
                                <button *ngIf="message.is_my_message" 
                                        class="btn-icon-sm" 
                                        (click)="deleteMessage(message)"
                                        title="Delete">
                                    <img src="assets/messaging/ic_delete.svg" alt="Delete">
                                </button>
                            </div>
                        </div>
                        
                        <!-- Attachments -->
                        <div *ngIf="!message.is_message_deleted && message.attachments && message.attachments.length > 0" class="message-image-attachments">
                            <div *ngFor="let attachment of message.attachments" class="attachment-item">
                                <!-- Attachment Actions -->
                                <div class="message-actions">
                                    <button class="btn-icon-sm" (click)="replyToMessage(message, attachment)" title="Reply">
                                        <img src="assets/messaging/ic_reply.svg" alt="Reply">
                                    </button>
                                    <button class="btn-icon-sm attachment-reaction-btn" 
                                            (click)="toggleAttachmentReactionPicker(attachment.chat_message_attachment_id, $event)"
                                            [class.active]="showAttachmentReactionPicker === attachment.chat_message_attachment_id"
                                            title="Add reaction">
                                        <img src="assets/messaging/ic_emoji.svg" alt="Add reaction">
                                    </button>
                                    <button *ngIf="message.is_my_message" 
                                            class="btn-icon-sm" 
                                            (click)="deleteAttachment(attachment)"
                                            title="Delete">
                                        <img src="assets/messaging/ic_delete.svg" alt="Delete">
                                    </button>
                                </div>

                                <!-- Object Attachments (lesson, course, document, folder) -->
                                <ng-container *ngIf="isObjectAttachment(attachment)">
                                    <ng-container *ngTemplateOutlet="objectAttachmentCard; context: { attachment: attachment }"></ng-container>
                                </ng-container>

                                <!-- File Attachments (non-images and non-videos) -->
                                <div *ngIf="!isObjectAttachment(attachment) && !isImageAttachment(attachment) && !isVideoAttachment(attachment)" 
                                     class="message-bubble attachment-file-bubble"
                                     [ngClass]="getAttachmentIconClass(attachment)">
                                    <div class="message-content">
                                        <a [href]="attachment.attachment?.url" target="_blank" [download]="attachment.attachment?.name">
                                            <i [class]="getAttachmentIcon(attachment)"></i>
                                            {{ attachment.attachment?.name }}.{{ attachment.attachment?.extension }}
                                        </a>
                                    </div>
                                </div>

                                <!-- Image Attachments -->
                                <div *ngIf="isImageAttachment(attachment)" class="message-image-container">
                                    <img [src]="attachment.attachment.url" 
                                         [alt]="attachment.attachment.name || 'Message image'"
                                         class="message-image"
                                         [class.message-image-clickable]="!isGifAttachment(attachment)"
                                         [class.message-gif]="isGifAttachment(attachment)"
                                         (click)="!isGifAttachment(attachment) && openMessageImageModal(attachment)"
                                         loading="lazy">
                                </div>
                                
                                <!-- Video Attachments -->
                                <div *ngIf="isVideoAttachment(attachment)" class="message-image-container">
                                    <div class="message-video-thumbnail" (click)="openMessageImageModal(attachment)">
                                        <img [src]="attachment.attachment.thumbnail || attachment.attachment.url" 
                                             [alt]="attachment.attachment.name || 'Message video'"
                                             class="message-image"
                                             loading="lazy">
                                        <div class="video-play-overlay">
                                            <img src="../../../../assets/common/ic_play_circle.svg" />
                                        </div>
                                    </div>
                                </div>

                                <!-- Attachment Reactions -->
                                <div *ngIf="attachment.reactions && attachment.reactions.length > 0" class="message-reactions attachment-reactions mt-1">
                                    <button *ngFor="let reaction of attachment.reactions" 
                                            class="reaction-button"
                                            [class.reaction-active]="reaction.is_user_reaction"
                                            [title]="reaction.count + ' reaction' + (reaction.count !== 1 ? 's' : '')"
                                            (click)="toggleAttachmentReaction(attachment, reaction.content)">
                                        <span class="reaction-emoji">{{ reaction.content }}</span>
                                        <span class="reaction-count">{{ reaction.count }}</span>
                                    </button>
                                    <button
                                        type="button"
                                        class="message-reactions-link attachment-reactions-link"
                                        (click)="openAttachmentReactions(attachment)">
                                        Reactions
                                    </button>
                                </div>

                                <!-- Attachment Reaction Picker Popup -->
                                <div *ngIf="showAttachmentReactionPicker === attachment.chat_message_attachment_id" 
                                     class="reaction-picker-popup attachment-reaction-picker-popup"
                                     [attr.data-attachment-reaction-popup]="attachment.chat_message_attachment_id">
                                    <div class="reaction-picker-grid">
                                        <button *ngFor="let emoji of availableReactions" 
                                                class="reaction-picker-item"
                                                (click)="toggleAttachmentReaction(attachment, emoji)"
                                                [title]="emoji">
                                            {{ emoji }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Message Reactions -->
                        <div *ngIf="!message.is_message_deleted && message.reactions && message.reactions.length > 0" class="message-reactions">
                            <button *ngFor="let reaction of message.reactions" 
                                    class="reaction-button"
                                    [class.reaction-active]="reaction.is_user_reaction"
                                    [title]="reaction.count + ' reaction' + (reaction.count !== 1 ? 's' : '')"
                                    (click)="toggleReaction(message, reaction.content)">
                                <span class="reaction-emoji">{{ reaction.content }}</span>
                                <span class="reaction-count">{{ reaction.count }}</span>
                            </button>
                            <button
                                type="button"
                                class="message-reactions-link"
                                (click)="openMessageReactions(message)">
                                Reactions
                            </button>
                        </div>
                        
                        <div class="message-meta">
                            <span class="message-time">{{ message.created_at | dateFormat:'h:mm a' }}</span>
                            <!--<span class="message-time-ago">{{ getTimeAgo(message.created_at) }}</span>-->
                        </div>
                    </div>
                    
                    <!-- Reaction Picker Popup -->
                    <div *ngIf="showReactionPicker === message.id" 
                         class="reaction-picker-popup"
                         [attr.data-reaction-popup]="message.id"
                         #reactionPickerPopup>
                        <div class="reaction-picker-grid">
                            <button *ngFor="let emoji of availableReactions" 
                                    class="reaction-picker-item"
                                    (click)="toggleReaction(message, emoji)"
                                    [title]="emoji">
                                {{ emoji }}
                            </button>
                        </div>
                    </div>
                    
                    <!-- Owner/Coach Avatar (right side for owner messages) -->
                    <div *ngIf="message.is_my_message" class="message-avatar">
                        <img *ngIf="message.author.avatar" 
                             [src]="message.author.avatar" 
                             [alt]="getOwnerName(message)"
                             class="avatar-image">
                        <div *ngIf="!message.author.avatar" class="avatar-circle">
                            <span class="avatar-initials">{{ getOwnerInitials(message) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Thread Replies -->
                <ng-container *ngIf="message.thread && message.thread.length > 0">
                    <div *ngFor="let threadMessage of message.thread; let i = index" 
                         class="message-item message-thread" 
                         [ngClass]="{
                             'message-owner': message.is_my_message, 
                             'message-user': !message.is_my_message,
                             'thread-sender-owner': threadMessage.is_my_message,
                             'thread-sender-user': !threadMessage.is_my_message
                         }">
                        <!-- Thread Reply Label (only on first message) -->
                        <div class="thread-reply-label" *ngIf="i === 0 && getThreadReplyLabel(message)">
                            <i class="icon icon-reply"></i>
                            <span>{{ getThreadReplyLabel(message) }}</span>
                        </div>
                        
                        <!-- User Avatar (for thread messages from user) -->
                        <div *ngIf="!threadMessage.is_my_message" class="message-avatar">
                            <img *ngIf="threadMessage.author.avatar" 
                                 [src]="threadMessage.author.avatar" 
                                 [alt]="threadMessage.author.name"
                                 class="avatar-image">
                            <div *ngIf="!threadMessage.author.avatar" class="avatar-circle">
                                <span class="avatar-initials">{{ getMessageInitials(threadMessage) }}</span>
                            </div>
                        </div>
                        
                        <div class="message-wrapper">
                            <!-- Message Bubble Row (bubble + actions) -->
                            <div class="message-bubble-row" *ngIf="(threadMessage.message || (!threadMessage.attachments || threadMessage.attachments.length === 0 || !hasImageAttachments(threadMessage)))">
                                <!-- Text Message Bubble -->
                                <div class="message-bubble" 
                                     [ngClass]="{'message-deleted': threadMessage.is_message_deleted}">
                                    <div class="message-content">
                                        <p *ngIf="!threadMessage.is_message_deleted && threadMessage.message" 
                                            [innerHTML]="getMessageWithLinks(threadMessage.message)">
                                        </p>
                                        
                                        <div *ngIf="threadMessage.is_message_deleted" class="deleted-message-indicator">
                                            <i class="ti ti-ban"></i>
                                            <span>{{ threadMessage.message }}</span>
                                        </div>
                                        
                                        <p *ngIf="!threadMessage.is_message_deleted && !threadMessage.message && (!threadMessage.attachments || threadMessage.attachments.length === 0)" 
                                           class="text-muted deleted-message">
                                            Attachment deleted
                                        </p>
                                    </div>
                                </div>
                                
                                <!-- Message Actions -->
                                <div class="message-actions" *ngIf="!threadMessage.is_message_deleted">
                                    <button class="btn-icon-sm" (click)="replyToMessage(threadMessage)" title="Reply">
                                        <i class="icon icon-reply"></i>
                                    </button>
                                    <!--
                                    <button *ngIf="!threadMessage.is_my_message && threadMessage.is_message_read !== false" 
                                            class="btn-icon-sm icon-mark-unread" 
                                            (click)="markMessageAsUnread(threadMessage)"
                                            title="Mark as unread">
                                        <img src="assets/messaging/ic_mark_as_unread.svg" alt="Mark as unread">
                                    </button>
                                    -->
                                    <button class="btn-icon-sm" 
                                            (click)="toggleReactionPicker(threadMessage.id, $event)"
                                            [class.active]="showReactionPicker === threadMessage.id"
                                            title="Add reaction">
                                        <i class="icon icon-add-reaction"></i>
                                    </button>
                                    <button *ngIf="threadMessage.is_my_message" 
                                            class="btn-icon-sm" 
                                            (click)="deleteMessage(threadMessage)"
                                            title="Delete">
                                        <i class="icon icon-delete"></i>
                                    </button>
                                </div>
                            </div>
                            
                            <!-- Image, Video and File Attachments (outside bubble) -->
                            <div *ngIf="!threadMessage.is_message_deleted && threadMessage.attachments && threadMessage.attachments.length > 0" 
                                 class="message-image-attachments">
                                <div *ngFor="let attachment of threadMessage.attachments" class="attachment-item">
                                    <div class="message-actions">
                                        <button class="btn-icon-sm" (click)="replyToMessage(threadMessage, attachment)" title="Reply">
                                            <i class="icon icon-reply"></i>
                                        </button>
                                        <button class="btn-icon-sm attachment-reaction-btn" 
                                                (click)="toggleAttachmentReactionPicker(attachment.chat_message_attachment_id, $event)"
                                                [class.active]="showAttachmentReactionPicker === attachment.chat_message_attachment_id"
                                                title="Add reaction">
                                            <i class="icon icon-add-reaction"></i>
                                        </button>
                                        <button *ngIf="threadMessage.is_my_message" 
                                                class="btn-icon-sm" 
                                                (click)="deleteAttachment(attachment)"
                                                title="Delete">
                                            <i class="icon icon-delete"></i>
                                        </button>
                                    </div>

                                    <!-- Object Attachments (lesson, course, document, folder) -->
                                    <ng-container *ngIf="isObjectAttachment(attachment)">
                                        <ng-container *ngTemplateOutlet="objectAttachmentCard; context: { attachment: attachment }"></ng-container>
                                    </ng-container>

                                    <!-- File Attachments (non-images and non-videos) -->
                                    <div *ngIf="!isObjectAttachment(attachment) && !isImageAttachment(attachment) && !isVideoAttachment(attachment)" 
                                         class="message-bubble attachment-file-bubble"
                                         [ngClass]="getAttachmentIconClass(attachment)">
                                        <div class="message-content">
                                            <a [href]="attachment.attachment?.url" target="_blank" [download]="attachment.attachment?.name">
                                                <i [class]="getAttachmentIcon(attachment)"></i>
                                                {{ attachment.attachment?.name }}.{{ attachment.attachment?.extension }}
                                            </a>
                                        </div>
                                    </div>

                                    <div *ngIf="isImageAttachment(attachment)" class="message-image-container">
                                        <img [src]="attachment.attachment.url" 
                                             [alt]="attachment.attachment.name || 'Message image'"
                                             class="message-image"
                                             [class.message-image-clickable]="!isGifAttachment(attachment)"
                                             [class.message-gif]="isGifAttachment(attachment)"
                                             (click)="!isGifAttachment(attachment) && openMessageImageModal(attachment)"
                                             loading="lazy">
                                    </div>
                                    <div *ngIf="isVideoAttachment(attachment)" class="message-image-container">
                                        <div class="message-video-thumbnail" (click)="openMessageImageModal(attachment)">
                                            <img [src]="attachment.attachment.thumbnail || attachment.attachment.url" 
                                                 [alt]="attachment.attachment.name || 'Message video'"
                                                 class="message-image"
                                                 loading="lazy">
                                            <div class="video-play-overlay">
                                                <img src="../../../../assets/common/ic_play_circle.svg" />
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Attachment Reactions -->
                                    <div *ngIf="attachment.reactions && attachment.reactions.length > 0" class="message-reactions attachment-reactions mt-1">
                                        <button *ngFor="let reaction of attachment.reactions" 
                                                class="reaction-button"
                                                [class.reaction-active]="reaction.is_user_reaction"
                                                [title]="reaction.count + ' reaction' + (reaction.count !== 1 ? 's' : '')"
                                                (click)="toggleAttachmentReaction(attachment, reaction.content)">
                                            <span class="reaction-emoji">{{ reaction.content }}</span>
                                            <span class="reaction-count">{{ reaction.count }}</span>
                                        </button>
                                        <button
                                            type="button"
                                            class="message-reactions-link attachment-reactions-link"
                                            (click)="openAttachmentReactions(attachment)">
                                            Reactions
                                        </button>
                                    </div>

                                    <!-- Attachment Reaction Picker Popup -->
                                    <div *ngIf="showAttachmentReactionPicker === attachment.chat_message_attachment_id" 
                                         class="reaction-picker-popup attachment-reaction-picker-popup"
                                         [attr.data-attachment-reaction-popup]="attachment.chat_message_attachment_id">
                                        <div class="reaction-picker-grid">
                                            <button *ngFor="let emoji of availableReactions" 
                                                    class="reaction-picker-item"
                                                    (click)="toggleAttachmentReaction(attachment, emoji)"
                                                    [title]="emoji">
                                                {{ emoji }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Message Reactions (outside bubble) -->
                            <div *ngIf="!threadMessage.is_message_deleted && threadMessage.reactions && threadMessage.reactions.length > 0" class="message-reactions">
                                <button *ngFor="let reaction of threadMessage.reactions" 
                                        class="reaction-button"
                                        [class.reaction-active]="reaction.is_user_reaction"
                                        [title]="reaction.count + ' reaction' + (reaction.count !== 1 ? 's' : '')"
                                        (click)="toggleReaction(threadMessage, reaction.content)">
                                    <span class="reaction-emoji">{{ reaction.content }}</span>
                                    <span class="reaction-count">{{ reaction.count }}</span>
                                </button>
                                <button
                                    type="button"
                                    class="message-reactions-link"
                                    (click)="openMessageReactions(threadMessage)">
                                    Reactions
                                </button>
                            </div>
                            
                            <div class="message-meta">
                                <span class="message-time">{{ threadMessage.created_at | dateFormat:'h:mm a' }}</span>
                                <!--<span class="message-time-ago">{{ getTimeAgo(threadMessage.created_at) }}</span>-->
                            </div>
                        </div>
                        
                        <!-- Reaction Picker Popup for Thread Messages -->
                        <div *ngIf="showReactionPicker === threadMessage.id" 
                             class="reaction-picker-popup"
                             [attr.data-reaction-popup]="threadMessage.id"
                             #reactionPickerPopup>
                            <div class="reaction-picker-grid">
                                <button *ngFor="let emoji of availableReactions" 
                                        class="reaction-picker-item"
                                        (click)="toggleReaction(threadMessage, emoji)"
                                        [title]="emoji">
                                    {{ emoji }}
                                </button>
                            </div>
                        </div>
                        
                        <!-- Owner/Coach Avatar (for thread messages from owner) -->
                        <div *ngIf="threadMessage.is_my_message" class="message-avatar">
                            <img *ngIf="threadMessage.author.avatar" 
                                 [src]="threadMessage.author.avatar" 
                                 [alt]="getOwnerName(threadMessage)"
                                 class="avatar-image">
                            <div *ngIf="!threadMessage.author.avatar" class="avatar-circle">
                                <span class="avatar-initials">{{ getOwnerInitials(threadMessage) }}</span>
                            </div>
                        </div>
                    </div>
                </ng-container>
                <!-- End of Message Item -->
            </ng-container>
        </div>
        }

        <!-- Scheduled Messages Panel -->
        <div *ngIf="scheduledMessages && scheduledMessages.length > 0" class="scheduled-messages-panel">
            <div class="scheduled-header">
                <div class="scheduled-title">
                    <i class="ti ti-clock"></i>
                    <span>Scheduled messages ({{ scheduledMessages.length }})</span>
                </div>
                <button
                    *ngIf="scheduledMessages.length > 1"
                    type="button"
                    class="btn-link"
                    (click)="onViewAllScheduledMessages()">
                    View all
                </button>
            </div>

            <div class="scheduled-card">
                <ng-container *ngIf="scheduledMessages[0] as scheduled">
                    <div class="scheduled-main">
                        <div class="scheduled-datetime">
                            {{ (scheduled.scheduled_at || scheduled.created_at) | dateFormat:'EEE, MMM d y' }}
                            at 
                            {{ (scheduled.scheduled_at || scheduled.created_at) | dateFormat:'h:mm a' }}
                        </div>
                        <div class="scheduled-text">
                            {{ scheduled.message || 'Scheduled message' }}
                        </div>
                    </div>

                    <div class="scheduled-actions">
                        <button
                            type="button"
                            class="btn-icon-sm"
                            title="Edit message"
                            (click)="onEditScheduledMessage(scheduled)">
                            <img src="assets/common/ic_edit.svg" alt="Edit">
                        </button>
                        <button
                            type="button"
                            class="btn-icon-sm"
                            title="Reschedule"
                            (click)="onRescheduleScheduledMessage(scheduled)">
                            <img src="assets/common/ic_calendar_s_size.svg" alt="Reschedule">
                        </button>
                        <button
                            type="button"
                            class="btn-icon-sm"
                            title="Delete"
                            (click)="onDeleteScheduledMessage(scheduled)">
                            <img src="assets/common/ic_delete_destructive_s_size.svg" alt="Delete">
                        </button>
                    </div>
                </ng-container>
            </div>
        </div>

        <!-- Message Input Form -->
        <div class="message-input-container">
            <!-- Upload Progress -->
            <div *ngIf="uploadingFiles" class="upload-progress">
                <div class="upload-progress-text">
                    Uploading {{ currentUploadIndex + 1 }}/{{ totalUploads }}
                </div>
                <div class="upload-progress-bar">
                    <div class="upload-progress-fill" [style.width.%]="uploadProgress"></div>
                </div>
            </div>

            <!-- Uploaded Files Thumbnails -->
            <div *ngIf="uploadedFiles.length > 0" class="uploaded-files-preview">
                <div *ngFor="let file of uploadedFiles" 
                     class="uploaded-file-thumbnail"
                     [ngClass]="{'thumbnail-document': file.type === 'document'}">

                    <img *ngIf="file.type === 'image'" 
                         [src]="file.thumbnail_url || file.url" 
                         [alt]="file.name"
                         class="thumbnail-image">

                    <img *ngIf="file.type === 'video'" 
                         [src]="file.thumbnail || 'assets/icons/video-placeholder.png'" 
                         [alt]="file.name"
                         class="thumbnail-video">

                    <div *ngIf="file.type === 'document'" class="thumbnail-document-content" [ngClass]="getFileTypeClass(file)">
                        <i [class]="'ti ' + getFileTypeIcon(file)"></i>
                        <span class="document-filename" [title]="file.name">{{ file.name }}</span>
                    </div>

                    <button type="button" 
                            class="thumbnail-remove-btn" 
                            (click)="onRemoveAttachment(file)"
                            title="Remove">
                        <i class="ti ti-x"></i>
                    </button>
                </div>
            </div>

            <!-- Emoji Picker -->
            <div *ngIf="showEmojiPicker" class="emoji-picker-container">
                <emoji-mart
                    title="Emojis"
                    emoji="point_up"
                    (emojiSelect)="onEmojiSelect($event)"
                    [isNative]="true"
                    [perLine]="8"
                    [skin]="1"
                    [sheetSize]="32">
                </emoji-mart>
            </div>

            <!-- GIF Picker -->
            <div *ngIf="showGifPicker && context !== 'broadcast'" class="gif-picker-container" #gifPickerContainer>
                <div class="gif-picker-header">
                    <h6 class="mb-0">GIFs</h6>
                    <button type="button" class="btn-icon-sm" (click)="closeGifPicker()" title="Close">
                        <i class="ti ti-x"></i>
                    </button>
                </div>
                <div class="gif-picker-search">
                    <input type="text" 
                           class="form-control" 
                           placeholder="Search GIFs"
                           [(ngModel)]="gifSearchQuery"
                           (ngModelChange)="onGifSearchChange($event)">
                    <i class="ti ti-search gif-search-icon"></i>
                </div>
                <div class="gif-picker-content">
                    <div *ngIf="loadingGifs && gifs.length === 0" class="gif-loading text-center py-4">
                        <p class="text-muted">Loading GIFs...</p>
                    </div>
                    <div *ngIf="!loadingGifs && gifs.length === 0 && gifSearchQuery.trim()" class="gif-empty text-center py-4">
                        <p class="text-muted">No GIFs found</p>
                    </div>
                    <div class="gif-grid" *ngIf="gifs.length > 0">
                        <div *ngFor="let gif of gifs" 
                             class="gif-item" 
                             (click)="onGifSelect(gif)"
                             [title]="gif.title || 'GIF'">
                            <img [src]="gif.images?.fixed_height?.url || gif.images?.original?.url" 
                                 [alt]="gif.title || 'GIF'"
                                 loading="lazy">
                        </div>
                    </div>
                    <div *ngIf="loadingGifs && gifs.length > 0" class="gif-loading-more text-center py-2">
                        <p class="text-muted small">Loading more...</p>
                    </div>
                    <button *ngIf="hasMoreGifs && !loadingGifs && gifs.length > 0" 
                            class="btn btn-sm btn-outline-primary w-100 mt-2"
                            (click)="loadMoreGifs(); $event.stopPropagation()">
                        Load More
                    </button>
                </div>
            </div>

            <form #messageForm="ngForm" (ngSubmit)="isEditingScheduledMessage() ? confirmScheduledMessageEdit() : sendMessage()">
                <input type="file" 
                       #fileInput
                       accept="image/*"
                       multiple
                       style="display: none;"
                       (change)="onFileSelected($event, 'image')">
                
                <input type="file"
                       #videoInput
                       accept="video/*"
                       multiple
                       style="display: none;"
                       (change)="onFileSelected($event, 'video')">
                
                <input type="file" 
                       #documentInput
                       accept=".pdf,.xls,.xlsx,.csv,.doc,.docx,.txt"
                       multiple
                       style="display: none;"
                       (change)="onFileSelected($event, 'document')">
                
                <!-- Reply Balloon -->
                <div *ngIf="replyingToMessage" class="reply-balloon">
                    <div class="reply-balloon-content">
                        <div class="reply-balloon-header">
                            <i class="icon icon-reply"></i>
                            <span>Replying to {{ replyingToMessage.is_my_message ? 'yourself' : getAuthorFirstName(replyingToMessage.author) }}</span>
                            <button type="button" 
                                    class="btn-icon-sm reply-balloon-close" 
                                    (click)="cancelReply()"
                                    title="Cancel reply">
                                <i class="icon icon-x-circle"></i>
                            </button>
                        </div>
                        
                        <div class="reply-balloon-message">
                            <!-- Show specific attachment info if replying to an attachment -->
                            <div *ngIf="replyingToAttachment">
                                <p *ngIf="isObjectAttachment(replyingToAttachment)" class="text-muted">
                                    {{ getObjectAttachmentName(replyingToAttachment) }}
                                </p>
                                <p *ngIf="!isObjectAttachment(replyingToAttachment) && isImageAttachment(replyingToAttachment)" class="text-muted">
                                    Image
                                </p>
                                <p *ngIf="!isObjectAttachment(replyingToAttachment) && isVideoAttachment(replyingToAttachment)" class="text-muted">
                                    Video
                                </p>
                                <p *ngIf="!isObjectAttachment(replyingToAttachment) && !isImageAttachment(replyingToAttachment) && !isVideoAttachment(replyingToAttachment)" class="text-muted">
                                    Document
                                </p>
                            </div>
                            
                            <!-- Show message content if not replying to a specific attachment -->
                            <div *ngIf="!replyingToAttachment">
                                <p *ngIf="replyingToMessage.message">
                                    {{ replyingToMessage.message }}
                                </p>
                                <p *ngIf="!replyingToMessage.message && replyingToMessage.attachments && replyingToMessage.attachments.length > 0" class="text-muted">
                                    Attachment
                                </p>
                                <p *ngIf="!replyingToMessage.message && (!replyingToMessage.attachments || replyingToMessage.attachments.length === 0)" class="text-muted">
                                    Empty message
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="message-input-wrapper">
                    <textarea #messageInputRef
                              class="form-control message-input" 
                              name="messageText"
                              rows="1"
                              maxlength="4000"
                              [(ngModel)]="messageText"
                              [placeholder]="context === 'broadcast' && broadcastMessagePlaceholder ? broadcastMessagePlaceholder : 'Type your message'"
                              [disabled]="!source"
                              (keydown.enter)="onEnterKey($event)"
                              (input)="adjustMessageInputHeight()"></textarea>
                </div>
                <div class="message-input-actions">
                    <div class="attachment-options">
                        <button type="button" 
                                class="btn-icon" 
                                [attr.title]="getAttachmentButtonTitle('image')"
                                (click)="onAttachImageClick()"
                                [disabled]="isAttachmentButtonDisabled('image')">
                            <i class="icon icon-add-image icon-lg"></i>
                        </button>
                        <button type="button" 
                                class="btn-icon" 
                                [attr.title]="getAttachmentButtonTitle('video')"
                                (click)="onAttachVideoClick()"
                                [disabled]="isAttachmentButtonDisabled('video')">
                            <i class="icon icon-add-video icon-lg"></i>
                        </button>
                        <button type="button" 
                                class="btn-icon" 
                                [attr.title]="getAttachmentButtonTitle('document')"
                                (click)="onAttachDocumentClick()"
                                [disabled]="isAttachmentButtonDisabled('document')">
                            <i class="icon icon-add-document icon-lg"></i>
                        </button>
                        <button type="button" 
                                #emojiPickerButton
                                class="btn-icon" 
                                title="Add an emoji"
                                [class.active]="showEmojiPicker"
                                [disabled]="!source || isComposerEditMode()"
                                (click)="onEmojiClick()">
                            <i class="icon icon-add-reaction icon-lg"></i>
                        </button>
                        @if (context !== 'broadcast') {
                            <button type="button" 
                                    #gifPickerButton
                                    class="btn-icon" 
                                    title="Add a GIF"
                                    [class.active]="showGifPicker"
                                    [disabled]="!source || isComposerEditMode()"
                                    (click)="onGifClick()">
                                <i class="icon icon-add-animation icon-lg"></i>
                            </button>
                        }
                    </div>
                    <div class="send-actions">
                        <ng-container *ngIf="!isComposerEditMode(); else cancelEditBtn">
                            <button type="button" 
                                    class="btn-icon" 
                                    title="Schedule message"
                                    (click)="openScheduleModal()"
                                    [disabled]="isMessageSendDisabled() || !!replyingToMessage">
                                <i class="ti ti-clock"></i>
                            </button>
                        </ng-container>
                        <ng-template #cancelEditBtn>
                            <button type="button" 
                                    class="btn-icon" 
                                    [attr.title]="isEditingBroadcastMessage() ? 'Cancel editing broadcast' : 'Cancel editing'"
                                    (click)="cancelComposerEdit()">
                                <i class="ti ti-x"></i>
                            </button>
                        </ng-template>
                        <button type="submit" 
                                class="btn btn-primary send-button"
                                [disabled]="isComposerSubmitDisabled()">
                            <ng-container *ngIf="!isComposerEditMode(); else confirmIcon">
                                <i *ngIf="!isComposerSubmitDisabled()" class="icon icon-send icon-lg"></i>
                                <i *ngIf="isComposerSubmitDisabled()" class="icon icon-send-inactive icon-lg"></i>
                            </ng-container>
                            <ng-template #confirmIcon>
                                <i class="ti ti-check"></i>
                            </ng-template>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>

    <!-- Reactions Breakdown Modal -->
    <div
        *ngIf="showReactionsBreakdown"
        class="blur-modal-backdrop reactions-modal-backdrop"
        (click)="closeReactionsBreakdown()">
    </div>

    <div
        *ngIf="showReactionsBreakdown"
        class="mantis-modal reactions-modal"
        role="dialog"
        aria-modal="true">
        <div class="reactions-modal-content" (click)="$event.stopPropagation()">
            <div class="reactions-modal-header">
                <div class="reactions-modal-title">
                    {{ getTotalReactions(reactionsBreakdown) }} {{ getTotalReactions(reactionsBreakdown) > 1 ? 'reactions' : 'reaction' }}
                </div>
                <button
                    type="button"
                    class="reactions-modal-close"
                    (click)="closeReactionsBreakdown()"
                    title="Close">
                    <img src="assets/common/ic_x.svg" alt="Close">
                </button>
            </div>

            <div *ngIf="reactionsBreakdownLoading" class="reactions-modal-loading">
                Loading reactions...
            </div>

            <ng-container *ngIf="!reactionsBreakdownLoading">
                <div *ngIf="reactionsBreakdown?.reactions?.length; else noReactionsContent" class="reactions-modal-body">
                    <div class="reactions-summary-row">
                        <div
                            *ngFor="let reaction of reactionsBreakdown.reactions"
                            class="reactions-summary-item">
                            <span class="reactions-summary-emoji">{{ reaction.content }}</span>
                            <span class="reactions-summary-count">{{ reaction.count }}</span>
                        </div>
                    </div>

                    <div class="reactions-users-list">
                        <div
                            *ngFor="let user of reactionsBreakdown.users"
                            class="reactions-user-row">
                            <div class="reactions-user-info">
                                <div class="reactions-user-avatar" [class.has-avatar]="user.avatar">
                                    <img
                                        *ngIf="user.avatar"
                                        [src]="user.avatar"
                                        [alt]="user.name" />
                                    <span *ngIf="!user.avatar" class="reactions-user-initials">
                                        {{ user.name ? (user.name[0] | uppercase) : '?' }}
                                    </span>
                                </div>
                                <div class="reactions-user-name">
                                    {{ user.name }}
                                </div>
                            </div>
                            <div class="reactions-user-emoji">
                                {{ user.reaction }}
                            </div>
                        </div>
                    </div>
                </div>

                <ng-template #noReactionsContent>
                    <div class="reactions-modal-empty">
                        No reactions to display.
                    </div>
                </ng-template>
            </ng-container>
        </div>
    </div>
</div>
